import { useState } from "react";
import { useT, translateError } from "../i18n/hooks.ts";
import { removeColumnFromSeries, type DailySeries } from "../model/dailySeries.ts";
import type { SeriesScale } from "../model/seriesScale.ts";
import { addOutletPoint, deleteOutletPoint, renameOutletPoint } from "../model/projectEntities.ts";
import type { EvalHidProject } from "../model/types.ts";
import { ConfirmDeleteDialog } from "./ConfirmDeleteDialog.tsx";
import { Dialog } from "./Dialog.tsx";
import { PasteDailySeries } from "./PasteDailySeries.tsx";
import { TrashIcon } from "./TrashIcon.tsx";

/**
 * Outlet-point building blocks shared by the new-project wizard
 * (Step3Outlets.tsx) and the editor's own catalog (OutletPointsPanel.tsx) —
 * also used directly by SubBasinCanvas.tsx/SubBasinMap.tsx (EditOutletPointDialog,
 * for the "Observado" tab) and CalibrationPanel.tsx. Lives directly under
 * ui/ (not wizard/ or editor/) so none of those have to import it from
 * another — see project_evalhid_code_audit.md.
 */

/** Exported: also used by SubBasinCanvas.tsx/SubBasinMap.tsx/CalibrationPanel.tsx. */
export function EditOutletPointDialog({
  initialName,
  onSave,
  onClose,
  observedFlow,
  onChangeObservedFlow,
  scale,
}: {
  initialName: string;
  onSave: (name: string) => void;
  onClose: () => void;
  observedFlow?: DailySeries | null;
  /** The project's own scale (EvalHidProject.seriesScale) — absent only alongside `onChangeObservedFlow` (no "Observado" tab to need it). */
  scale?: SeriesScale;
  /**
   * Absent in the wizard's own outlet-points table (no project/series to
   * attach an observed series to yet) — also deliberately left `undefined`
   * by SubBasinMap.tsx/SubBasinCanvas.tsx while CalibrationPanel.tsx isn't
   * open (`calibrationMode`), even though a real project exists: the
   * "Observado" tab below is gated on this being present, and should only
   * be reachable "por estar en modo calibración" per the user's own
   * request (see project_evalhid_calibracion.md), not just on there being
   * a project to attach a series to.
   */
  onChangeObservedFlow?: (series: DailySeries | null) => void;
}) {
  const [name, setName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"data" | "observed">("data");
  const t = useT();
  const colIndex = (observedFlow?.columns.indexOf(initialName) ?? -1) + 1;
  return (
    <Dialog title={t("step3.editOutletPointTitle")} onClose={onClose}>
      {onChangeObservedFlow && (
        <div className="dialog-tabs">
          <button
            className={tab === "data" ? "tab-active" : undefined}
            onClick={() => setTab("data")}
          >
            {t("editSubBasinDialog.basicData")}
          </button>
          <button
            className={tab === "observed" ? "tab-active" : undefined}
            onClick={() => setTab("observed")}
          >
            {t("step3.observedSeries")}
          </button>
        </div>
      )}

      {tab === "data" && (
        <div className="step-form">
          <input value={name} onChange={(e) => setName(e.target.value)} />
          {error && <p className="error">{error}</p>}
          <button
            className="button button-primary"
            onClick={() => {
              try {
                onSave(name);
              } catch (err) {
                setError(translateError(err, t));
              }
            }}
          >
            {t("common.save")}
          </button>
          <button className="button" onClick={onClose}>
            {t("common.cancel")}
          </button>
        </div>
      )}

      {tab === "observed" && onChangeObservedFlow && (
        <PasteDailySeries
          title={t("step3.observedSeries")}
          showTitle={false}
          series={observedFlow ?? null}
          columnName={initialName}
          colIndex={colIndex}
          scale={scale ?? "daily"}
          onSeriesChange={(next) => onChangeObservedFlow(next)}
          onIndexChange={() => {
            // No-op: unlike SubBasin.precipCol/Aquifer.pumpingCol, the
            // column index isn't persisted separately — it's always
            // derived from `initialName` above (see storage.ts's
            // `observedFlow` field comment).
          }}
          onDeleteSeries={() => ({
            sharedWithOthers: false,
            confirm: () => onChangeObservedFlow(removeColumnFromSeries(observedFlow!, colIndex)),
          })}
        />
      )}
    </Dialog>
  );
}

/** Just the "add a new outlet point" form — split out of OutletPointsSection (Step3Outlets.tsx) so the editor tab (OutletPointsPanel.tsx) can place it above "Draw"/"Link GIS layer", while the wizard keeps it right under its own title. */
export function OutletPointsForm({
  project,
  onChange,
}: {
  project: EvalHidProject;
  onChange: (project: EvalHidProject) => void;
}) {
  const [outletName, setOutletName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const t = useT();

  function handleAddOutletPoint() {
    setError(null);
    try {
      onChange(addOutletPoint(project, outletName));
      setOutletName("");
    } catch (err) {
      setError(translateError(err, t));
    }
  }

  return (
    <>
      <div className="step-form">
        <input
          placeholder={t("common.name")}
          value={outletName}
          onChange={(e) => setOutletName(e.target.value)}
        />
        <button className="button" onClick={handleAddOutletPoint} disabled={!outletName.trim()}>
          {t("common.add")}
        </button>
      </div>
      {error && <p className="error">{error}</p>}
    </>
  );
}

/**
 * The outlet point catalog table (edit/delete) — the other half of
 * OutletPointsSection (Step3Outlets.tsx), see OutletPointsForm.
 *
 * `inline`: wizard-only — edits the name directly in the table, same
 * pattern as the sub-basins list a step earlier (Step1SubBasins.tsx's
 * ExistingSubBasinsStep), instead of opening EditOutletPointDialog. The
 * editor's own catalog (OutletPointsPanel.tsx) keeps the dialog — same
 * reason it doesn't inline-edit sub-basins either (SubBasinsPanel.tsx
 * opens EditSubBasinDialog): a click there needs to reach the fuller
 * editing surface a catalog tab affords, not just a name.
 */
export function OutletPointsTable({
  project,
  onChange,
  inline = false,
}: {
  project: EvalHidProject;
  onChange: (project: EvalHidProject) => void;
  inline?: boolean;
}) {
  const [editingOutlet, setEditingOutlet] = useState<number | null>(null);
  const [deletingOutlet, setDeletingOutlet] = useState<number | null>(null);
  const t = useT();

  const editingOutletObj = project.outletPoints.find((p) => p.outletCode === editingOutlet);
  const deletingOutletObj = project.outletPoints.find((p) => p.outletCode === deletingOutlet);

  function rename(outletCode: number, name: string) {
    if (!name.trim()) return;
    onChange(renameOutletPoint(project, outletCode, name));
  }

  return (
    <>
      {project.outletPoints.length > 0 && (
        <div className="table-scroll catalog-table-scroll catalog-table-scroll-capped">
          <table>
            <thead>
              <tr>
                <th>{t("common.name")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {project.outletPoints.map((p) => (
                <tr
                  key={p.outletCode}
                  onClick={inline ? undefined : () => setEditingOutlet(p.outletCode)}
                >
                  <td>
                    {inline ? (
                      <input
                        defaultValue={p.name}
                        onBlur={(e) => rename(p.outletCode, e.target.value)}
                      />
                    ) : (
                      p.name
                    )}
                  </td>
                  <td>
                    <button
                      className="button-icon button-danger"
                      title={t("step3.deleteOutletPointPermanent")}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeletingOutlet(p.outletCode);
                      }}
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editingOutletObj && (
        <EditOutletPointDialog
          initialName={editingOutletObj.name}
          onSave={(name) => {
            onChange(renameOutletPoint(project, editingOutletObj.outletCode, name));
            setEditingOutlet(null);
          }}
          onClose={() => setEditingOutlet(null)}
        />
      )}
      {deletingOutletObj && (
        <ConfirmDeleteDialog
          title={t("step3.deleteOutletPointDialogTitle")}
          name={deletingOutletObj.name}
          onConfirm={() => onChange(deleteOutletPoint(project, deletingOutletObj.outletCode))}
          onClose={() => setDeletingOutlet(null)}
        />
      )}
    </>
  );
}
